import axiosInstance from '../../../config/axiosInstance';
import type {
  CreatePokerStudyBoardActionPayload,
  CreatePokerStudyBoardNodePayload,
  CreatePokerStudyBoardPayload,
  CreatePokerStudyNodeActionPayload,
  CopyPokerStudyPreflopRangeTemplatePayload,
  CreatePokerStudyPreflopRangeTemplatePayload,
  CreatePokerStudySpotPayload,
  CreatePokerStudySpotStrategyBoardFamilyPayload,
  CreatePokerStudySpotStrategyLineActionPayload,
  CreatePokerStudySpotStrategyLineBoardPayload,
  CreatePokerStudySpotStrategyLinePayload,
  CreatePokerStudySpotTheoryActionPayload,
  CreatePokerStudySpotTheoryBoardPatternPayload,
  CreatePokerStudySpotTheoryNodePayload,
  CreatePokerStudyTheoryBlockPayload,
  PokerStudyBoard,
  PokerStudyBoardAction,
  PokerStudyBoardDetail,
  PokerStudyBoardListItem,
  PokerStudyBoardNode,
  PokerStudyBoardNotes,
  PokerStudyBoardPreflopRange,
  PokerStudyAccessContext,
  PokerStudyBoardWorkspace,
  PokerStudyNodeAction,
  PokerStudyNodeNotes,
  PokerStudyNodeRangeState,
  PokerStudyPreflopRangeTemplate,
  PokerStudyRangeState,
  PokerStudySpot,
  PokerStudySpotDetail,
  PokerStudySpotListItem,
  PokerStudySpotNotes,
  PokerStudySpotPreflopRange,
  PokerStudySpotStrategy,
  PokerStudySpotStrategyBoardFamily,
  PokerStudySpotStrategyLine,
  PokerStudySpotStrategyLineAction,
  PokerStudySpotStrategyLineBoard,
  PokerStudySpotTheoryAction,
  PokerStudySpotTheoryBoardPattern,
  PokerStudySpotTheoryNode,
  PokerStudySpotTheoryTree,
  PokerStudyTheoryBlock,
  UpdatePokerStudyBoardPayload,
  UpdatePokerStudyBoardNodePayload,
  UpdatePokerStudyNodeActionPayload,
  UpdatePokerStudyPreflopRangeTemplatePayload,
  UpdatePokerStudySpotPayload,
  UpdatePokerStudySpotStrategyBoardFamilyPayload,
  UpdatePokerStudySpotStrategyLineActionPayload,
  UpdatePokerStudySpotStrategyLinePayload,
  UpdatePokerStudySpotTheoryActionPayload,
  UpdatePokerStudySpotTheoryBoardPatternPayload,
  UpdatePokerStudySpotTheoryNodePayload,
  UpsertPokerStudyBoardNotesPayload,
  UpsertPokerStudyBoardPreflopRangePayload,
  UpsertPokerStudyNodeNotesPayload,
  UpsertPokerStudyNodeRangeStatePayload,
  UpsertPokerStudySpotNotesPayload,
  UpsertPokerStudySpotPreflopRangePayload,
  UpsertPokerStudyRangeStatePayload,
} from '../types/pokerStudy';

export async function getPokerStudyAccessContext(): Promise<PokerStudyAccessContext> {
  const { data } = await axiosInstance.get('/poker-study/access/context');
  return data as PokerStudyAccessContext;
}

export async function listPokerStudySpots(): Promise<PokerStudySpotListItem[]> {
  const { data } = await axiosInstance.get('/poker-study/spots');
  return data as PokerStudySpotListItem[];
}

export async function listPokerStudyPreflopRangeTemplates(): Promise<
  PokerStudyPreflopRangeTemplate[]
> {
  const { data } = await axiosInstance.get('/poker-study/range-templates');
  return data as PokerStudyPreflopRangeTemplate[];
}

export async function createPokerStudyPreflopRangeTemplate(
  payload: CreatePokerStudyPreflopRangeTemplatePayload,
): Promise<PokerStudyPreflopRangeTemplate> {
  const { data } = await axiosInstance.post('/poker-study/range-templates', payload);
  return data as PokerStudyPreflopRangeTemplate;
}

export async function copyPokerStudyPreflopRangeTemplate(
  templateId: string,
  payload: CopyPokerStudyPreflopRangeTemplatePayload,
): Promise<PokerStudyPreflopRangeTemplate> {
  const { data } = await axiosInstance.post(
    `/poker-study/range-templates/${templateId}/copy`,
    payload,
  );
  return data as PokerStudyPreflopRangeTemplate;
}

export async function updatePokerStudyPreflopRangeTemplate(
  templateId: string,
  payload: UpdatePokerStudyPreflopRangeTemplatePayload,
): Promise<PokerStudyPreflopRangeTemplate> {
  const { data } = await axiosInstance.put(
    `/poker-study/range-templates/${templateId}`,
    payload,
  );
  return data as PokerStudyPreflopRangeTemplate;
}

export async function deletePokerStudyPreflopRangeTemplate(
  templateId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/range-templates/${templateId}`);
}

export async function createPokerStudySpot(
  payload: CreatePokerStudySpotPayload,
): Promise<PokerStudySpot> {
  const { data } = await axiosInstance.post('/poker-study/spots', payload);
  return data as PokerStudySpot;
}

export async function getPokerStudySpot(
  spotId: string,
): Promise<PokerStudySpot> {
  const { data } = await axiosInstance.get(`/poker-study/spots/${spotId}`);
  return data as PokerStudySpot;
}

export async function getPokerStudySpotDetail(
  spotId: string,
): Promise<PokerStudySpotDetail> {
  const { data } = await axiosInstance.get(`/poker-study/spots/${spotId}/detail`);
  return data as PokerStudySpotDetail;
}

export async function updatePokerStudySpot(
  spotId: string,
  payload: UpdatePokerStudySpotPayload,
): Promise<PokerStudySpot> {
  const { data } = await axiosInstance.put(`/poker-study/spots/${spotId}`, payload);
  return data as PokerStudySpot;
}

export async function getPokerStudySpotNotes(
  spotId: string,
): Promise<PokerStudySpotNotes | null> {
  const { data } = await axiosInstance.get(`/poker-study/spots/${spotId}/notes`);
  return data as PokerStudySpotNotes | null;
}

export async function upsertPokerStudySpotNotes(
  spotId: string,
  payload: UpsertPokerStudySpotNotesPayload,
): Promise<PokerStudySpotNotes> {
  const { data } = await axiosInstance.put(`/poker-study/spots/${spotId}/notes`, payload);
  return data as PokerStudySpotNotes;
}

export async function listPokerStudySpotPreflopRanges(
  spotId: string,
): Promise<PokerStudySpotPreflopRange[]> {
  const { data } = await axiosInstance.get(`/poker-study/spots/${spotId}/preflop-ranges`);
  return data as PokerStudySpotPreflopRange[];
}

export async function upsertPokerStudySpotPreflopRange(
  spotId: string,
  rangeKey: string,
  payload: UpsertPokerStudySpotPreflopRangePayload,
): Promise<PokerStudySpotPreflopRange> {
  const { data } = await axiosInstance.put(
    `/poker-study/spots/${spotId}/preflop-ranges/${rangeKey}`,
    payload,
  );
  return data as PokerStudySpotPreflopRange;
}

export async function deletePokerStudySpot(
  spotId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/spots/${spotId}`);
}

export async function createPokerStudyTheoryBlock(
  spotId: string,
  payload: CreatePokerStudyTheoryBlockPayload,
): Promise<PokerStudyTheoryBlock> {
  const { data } = await axiosInstance.post(
    `/poker-study/spots/${spotId}/theory-blocks`,
    payload,
  );
  return data as PokerStudyTheoryBlock;
}

export async function getPokerStudySpotTheoryTree(
  spotId: string,
): Promise<PokerStudySpotTheoryTree> {
  const { data } = await axiosInstance.get(`/poker-study/spots/${spotId}/theory-tree`);
  return data as PokerStudySpotTheoryTree;
}

export async function getPokerStudySpotStrategy(
  spotId: string,
): Promise<PokerStudySpotStrategy> {
  const { data } = await axiosInstance.get(`/poker-study/spots/${spotId}/strategy`);
  return data as PokerStudySpotStrategy;
}

export async function createPokerStudySpotFlopDecisionPoint(
  spotId: string,
): Promise<PokerStudySpotStrategy['decision_points'][number]> {
  const { data } = await axiosInstance.post(
    `/poker-study/spots/${spotId}/decision-points/flop-root`,
  );
  return data as PokerStudySpotStrategy['decision_points'][number];
}

export async function createPokerStudySpotStrategyLine(
  decisionPointId: string,
  payload: CreatePokerStudySpotStrategyLinePayload,
): Promise<PokerStudySpotStrategyLine> {
  const { data } = await axiosInstance.post(
    `/poker-study/decision-points/${decisionPointId}/strategy-lines`,
    payload,
  );
  return data as PokerStudySpotStrategyLine;
}

export async function updatePokerStudySpotStrategyLine(
  lineId: string,
  payload: UpdatePokerStudySpotStrategyLinePayload,
): Promise<PokerStudySpotStrategyLine> {
  const { data } = await axiosInstance.put(
    `/poker-study/strategy-lines/${lineId}`,
    payload,
  );
  return data as PokerStudySpotStrategyLine;
}

export async function deletePokerStudySpotStrategyLine(
  lineId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/strategy-lines/${lineId}`);
}

export async function createPokerStudySpotStrategyLineAction(
  lineId: string,
  payload: CreatePokerStudySpotStrategyLineActionPayload,
): Promise<PokerStudySpotStrategyLineAction> {
  const { data } = await axiosInstance.post(
    `/poker-study/strategy-lines/${lineId}/actions`,
    payload,
  );
  return data as PokerStudySpotStrategyLineAction;
}

export async function createPokerStudyResponseDecisionPointFromAction(
  actionId: string,
): Promise<PokerStudySpotStrategy['decision_points'][number]> {
  const { data } = await axiosInstance.post(
    `/poker-study/strategy-line-actions/${actionId}/response-decision-point`,
  );
  return data as PokerStudySpotStrategy['decision_points'][number];
}

export async function updatePokerStudySpotStrategyLineAction(
  actionId: string,
  payload: UpdatePokerStudySpotStrategyLineActionPayload,
): Promise<PokerStudySpotStrategyLineAction> {
  const { data } = await axiosInstance.put(
    `/poker-study/strategy-line-actions/${actionId}`,
    payload,
  );
  return data as PokerStudySpotStrategyLineAction;
}

export async function deletePokerStudySpotStrategyLineAction(
  actionId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/strategy-line-actions/${actionId}`);
}

export async function createPokerStudySpotStrategyBoardFamily(
  lineId: string,
  payload: CreatePokerStudySpotStrategyBoardFamilyPayload,
): Promise<PokerStudySpotStrategyBoardFamily> {
  const { data } = await axiosInstance.post(
    `/poker-study/strategy-lines/${lineId}/board-families`,
    payload,
  );
  return data as PokerStudySpotStrategyBoardFamily;
}

export async function assignPokerStudyBoardToStrategyLine(
  lineId: string,
  payload: CreatePokerStudySpotStrategyLineBoardPayload,
): Promise<PokerStudySpotStrategyLineBoard> {
  const { data } = await axiosInstance.post(
    `/poker-study/strategy-lines/${lineId}/boards`,
    payload,
  );
  return data as PokerStudySpotStrategyLineBoard;
}

export async function deletePokerStudyStrategyLineBoard(
  lineBoardId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/strategy-line-boards/${lineBoardId}`);
}

export async function updatePokerStudySpotStrategyBoardFamily(
  familyId: string,
  payload: UpdatePokerStudySpotStrategyBoardFamilyPayload,
): Promise<PokerStudySpotStrategyBoardFamily> {
  const { data } = await axiosInstance.put(
    `/poker-study/strategy-board-families/${familyId}`,
    payload,
  );
  return data as PokerStudySpotStrategyBoardFamily;
}

export async function deletePokerStudySpotStrategyBoardFamily(
  familyId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/strategy-board-families/${familyId}`);
}

export async function createPokerStudySpotTheoryRootNode(
  spotId: string,
  payload: CreatePokerStudySpotTheoryNodePayload,
): Promise<PokerStudySpotTheoryNode> {
  const { data } = await axiosInstance.post(
    `/poker-study/spots/${spotId}/theory-nodes/root`,
    payload,
  );
  return data as PokerStudySpotTheoryNode;
}

export async function createPokerStudySpotTheoryChildNode(
  nodeId: string,
  payload: CreatePokerStudySpotTheoryNodePayload,
): Promise<PokerStudySpotTheoryNode> {
  const { data } = await axiosInstance.post(
    `/poker-study/spot-theory-nodes/${nodeId}/children`,
    payload,
  );
  return data as PokerStudySpotTheoryNode;
}

export async function updatePokerStudySpotTheoryNode(
  nodeId: string,
  payload: UpdatePokerStudySpotTheoryNodePayload,
): Promise<PokerStudySpotTheoryNode> {
  const { data } = await axiosInstance.put(
    `/poker-study/spot-theory-nodes/${nodeId}`,
    payload,
  );
  return data as PokerStudySpotTheoryNode;
}

export async function deletePokerStudySpotTheoryNode(
  nodeId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/spot-theory-nodes/${nodeId}`);
}

export async function createPokerStudySpotTheoryAction(
  nodeId: string,
  payload: CreatePokerStudySpotTheoryActionPayload,
): Promise<PokerStudySpotTheoryAction> {
  const { data } = await axiosInstance.post(
    `/poker-study/spot-theory-nodes/${nodeId}/actions`,
    payload,
  );
  return data as PokerStudySpotTheoryAction;
}

export async function updatePokerStudySpotTheoryAction(
  actionId: string,
  payload: UpdatePokerStudySpotTheoryActionPayload,
): Promise<PokerStudySpotTheoryAction> {
  const { data } = await axiosInstance.put(
    `/poker-study/spot-theory-actions/${actionId}`,
    payload,
  );
  return data as PokerStudySpotTheoryAction;
}

export async function deletePokerStudySpotTheoryAction(
  actionId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/spot-theory-actions/${actionId}`);
}

export async function createPokerStudySpotTheoryBoardPattern(
  spotId: string,
  payload: CreatePokerStudySpotTheoryBoardPatternPayload,
): Promise<PokerStudySpotTheoryBoardPattern> {
  const { data } = await axiosInstance.post(
    `/poker-study/spots/${spotId}/theory-board-patterns`,
    payload,
  );
  return data as PokerStudySpotTheoryBoardPattern;
}

export async function updatePokerStudySpotTheoryBoardPattern(
  patternId: string,
  payload: UpdatePokerStudySpotTheoryBoardPatternPayload,
): Promise<PokerStudySpotTheoryBoardPattern> {
  const { data } = await axiosInstance.put(
    `/poker-study/spot-theory-board-patterns/${patternId}`,
    payload,
  );
  return data as PokerStudySpotTheoryBoardPattern;
}

export async function deletePokerStudySpotTheoryBoardPattern(
  patternId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/spot-theory-board-patterns/${patternId}`);
}

export async function listPokerStudyBoards(): Promise<PokerStudyBoardListItem[]> {
  const { data } = await axiosInstance.get('/poker-study/boards');
  return data as PokerStudyBoardListItem[];
}

export async function createPokerStudyBoard(
  spotId: string,
  payload: CreatePokerStudyBoardPayload,
): Promise<PokerStudyBoard> {
  const { data } = await axiosInstance.post(
    `/poker-study/spots/${spotId}/boards`,
    payload,
  );
  return data as PokerStudyBoard;
}

export async function updatePokerStudyBoard(
  boardId: string,
  payload: UpdatePokerStudyBoardPayload,
): Promise<PokerStudyBoard> {
  const { data } = await axiosInstance.put(`/poker-study/boards/${boardId}`, payload);
  return data as PokerStudyBoard;
}

export async function deletePokerStudyBoard(
  boardId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/boards/${boardId}`);
}

export async function getPokerStudyBoardDetail(
  boardId: string,
): Promise<PokerStudyBoardDetail> {
  const { data } = await axiosInstance.get(`/poker-study/boards/${boardId}/detail`);
  return data as PokerStudyBoardDetail;
}

export async function listPokerStudyBoardPreflopRanges(
  boardId: string,
): Promise<PokerStudyBoardPreflopRange[]> {
  const { data } = await axiosInstance.get(`/poker-study/boards/${boardId}/preflop-ranges`);
  return data as PokerStudyBoardPreflopRange[];
}

export async function upsertPokerStudyBoardPreflopRange(
  boardId: string,
  rangeKey: string,
  payload: UpsertPokerStudyBoardPreflopRangePayload,
): Promise<PokerStudyBoardPreflopRange> {
  const { data } = await axiosInstance.put(
    `/poker-study/boards/${boardId}/preflop-ranges/${rangeKey}`,
    payload,
  );
  return data as PokerStudyBoardPreflopRange;
}

export async function upsertPokerStudyBoardNotes(
  boardId: string,
  payload: UpsertPokerStudyBoardNotesPayload,
): Promise<PokerStudyBoardNotes> {
  const { data } = await axiosInstance.put(`/poker-study/boards/${boardId}/notes`, payload);
  return data as PokerStudyBoardNotes;
}

export async function createPokerStudyBoardAction(
  boardId: string,
  payload: CreatePokerStudyBoardActionPayload,
): Promise<PokerStudyBoardAction> {
  const { data } = await axiosInstance.post(`/poker-study/boards/${boardId}/actions`, payload);
  return data as PokerStudyBoardAction;
}

export async function getPokerStudyBoardRangeState(
  boardId: string,
): Promise<PokerStudyRangeState> {
  const { data } = await axiosInstance.get(`/poker-study/boards/${boardId}/range-state`);
  return data as PokerStudyRangeState;
}

export async function upsertPokerStudyBoardRangeState(
  boardId: string,
  payload: UpsertPokerStudyRangeStatePayload,
): Promise<PokerStudyRangeState> {
  const { data } = await axiosInstance.put(`/poker-study/boards/${boardId}/range-state`, payload);
  return data as PokerStudyRangeState;
}

export async function getPokerStudyBoardWorkspace(
  boardId: string,
): Promise<PokerStudyBoardWorkspace> {
  const { data } = await axiosInstance.get(`/poker-study/boards/${boardId}/workspace`);
  return data as PokerStudyBoardWorkspace;
}

export async function createPokerStudyBoardNode(
  boardId: string,
  payload: CreatePokerStudyBoardNodePayload,
): Promise<PokerStudyBoardNode> {
  const { data } = await axiosInstance.post(`/poker-study/boards/${boardId}/nodes`, payload);
  return data as PokerStudyBoardNode;
}

export async function createPokerStudyBoardNodeChild(
  nodeId: string,
  payload: CreatePokerStudyBoardNodePayload,
): Promise<PokerStudyBoardNode> {
  const { data } = await axiosInstance.post(`/poker-study/nodes/${nodeId}/children`, payload);
  return data as PokerStudyBoardNode;
}

export async function updatePokerStudyBoardNode(
  nodeId: string,
  payload: UpdatePokerStudyBoardNodePayload,
): Promise<PokerStudyBoardNode> {
  const { data } = await axiosInstance.put(`/poker-study/nodes/${nodeId}`, payload);
  return data as PokerStudyBoardNode;
}

export async function deletePokerStudyBoardNode(
  nodeId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/nodes/${nodeId}`);
}

export async function listPokerStudyNodeActions(
  nodeId: string,
): Promise<PokerStudyNodeAction[]> {
  const { data } = await axiosInstance.get(`/poker-study/nodes/${nodeId}/actions`);
  return data as PokerStudyNodeAction[];
}

export async function createPokerStudyNodeAction(
  nodeId: string,
  payload: CreatePokerStudyNodeActionPayload,
): Promise<PokerStudyNodeAction> {
  const { data } = await axiosInstance.post(`/poker-study/nodes/${nodeId}/actions`, payload);
  return data as PokerStudyNodeAction;
}

export async function updatePokerStudyNodeAction(
  actionId: string,
  payload: UpdatePokerStudyNodeActionPayload,
): Promise<PokerStudyNodeAction> {
  const { data } = await axiosInstance.put(`/poker-study/node-actions/${actionId}`, payload);
  return data as PokerStudyNodeAction;
}

export async function deletePokerStudyNodeAction(
  actionId: string,
): Promise<void> {
  await axiosInstance.delete(`/poker-study/node-actions/${actionId}`);
}

export async function getPokerStudyNodeRangeState(
  nodeId: string,
): Promise<PokerStudyNodeRangeState> {
  const { data } = await axiosInstance.get(`/poker-study/nodes/${nodeId}/range-state`);
  return data as PokerStudyNodeRangeState;
}

export async function upsertPokerStudyNodeRangeState(
  nodeId: string,
  payload: UpsertPokerStudyNodeRangeStatePayload,
): Promise<PokerStudyNodeRangeState> {
  const { data } = await axiosInstance.put(`/poker-study/nodes/${nodeId}/range-state`, payload);
  return data as PokerStudyNodeRangeState;
}

export async function getPokerStudyNodeNotes(
  nodeId: string,
): Promise<PokerStudyNodeNotes | null> {
  const { data } = await axiosInstance.get(`/poker-study/nodes/${nodeId}/notes`);
  return data as PokerStudyNodeNotes | null;
}

export async function upsertPokerStudyNodeNotes(
  nodeId: string,
  payload: UpsertPokerStudyNodeNotesPayload,
): Promise<PokerStudyNodeNotes> {
  const { data } = await axiosInstance.put(`/poker-study/nodes/${nodeId}/notes`, payload);
  return data as PokerStudyNodeNotes;
}

export async function listPokerStudyNodeNotesByNodeIds(
  nodeIds: string[],
): Promise<PokerStudyNodeNotes[]> {
  const { data } = await axiosInstance.post('/poker-study/nodes/notes/by-node-ids', nodeIds);
  return data as PokerStudyNodeNotes[];
}